import { Button, Tooltip, mergeClasses } from "@fluentui/react-components";
import {
  ArrowDownloadRegular,
  OpenRegular,
  PlayRegular,
} from "@fluentui/react-icons";
import { ConstantValues } from "common/constants";
import { useOverflowTooltip } from "hooks/useOverflowTooltip";
import {
  useSharedCardDetailStyles,
  useSharedCardStyles,
  useSharedTagStyles,
} from "theme/sharedStyles";
import { convertDateStringToUnix, formatDate } from "utils/generalUtils";
import { ProjectActionsMenu } from "./ProjectActionsMenu";
import { useProjectCardStyles } from "./projectCard.styles";
import { IProjectCardProps } from "./projectCard.types";
import {
  PROJECT_TOOLTIPS,
  ProjectStatus,
  getStatusDisplayText,
} from "./projectConstants";
import {
  getProjectScoringState,
  getScoreButtonLabel,
  getScoreTooltip,
} from "./projectUtils";

const MAX_COLLABORATORS_DISPLAY = 3;

export const ProjectCard = (props: IProjectCardProps): JSX.Element => {
  const { project, onEdit, onScore, onDelete, onOpen, onExport } = props;
  const styles = useProjectCardStyles();
  const cardDetailStyles = useSharedCardDetailStyles();
  const sharedCardStyles = useSharedCardStyles();
  const tagStyles = useSharedTagStyles();

  const titleOverflow = useOverflowTooltip();
  const clientOverflow = useOverflowTooltip();
  const descOverflow = useOverflowTooltip();

  const updatedDate = formatDate(
    convertDateStringToUnix(project.modifiedAt ?? project.created),
  );

  const getStatusClass = (): string => {
    switch (project.status) {
      case ProjectStatus.Scored:
        return tagStyles.tagSuccess;
      case ProjectStatus.InProgress:
        return tagStyles.tagInfo;
      case ProjectStatus.Error:
        return tagStyles.tagError;
      default:
        return styles.statusNotScored;
    }
  };

  const getStatusDotClass = (): string => {
    switch (project.status) {
      case ProjectStatus.Scored:
        return styles.statusScoredDot;
      case ProjectStatus.InProgress:
        return styles.statusInProgressDot;
      case ProjectStatus.Error:
        return styles.statusErrorDot;
      default:
        return styles.statusNotScoredDot;
    }
  };

  const collaborators = (project.access ?? []).filter(
    (c) => c.userId !== project.createdBy?.userId,
  );
  const displayedCollaborators = collaborators.slice(
    0,
    MAX_COLLABORATORS_DISPLAY,
  );
  const remainingCount = collaborators.length - MAX_COLLABORATORS_DISPLAY;
  const collaboratorsText = displayedCollaborators
    .map((c) => c.displayName)
    .join(", ");
  const fullCollaboratorsText = collaborators
    .map((c) => c.displayName)
    .join(", ");

  const { isScored, isScoring, hasScoringError, canScore, canRetryScore } =
    getProjectScoringState(project);
  const isScoreButtonEnabled = canScore || canRetryScore;

  return (
    <div
      className={mergeClasses(
        sharedCardStyles.card,
        cardDetailStyles.cardOverride,
      )}
    >
      <div className={cardDetailStyles.headerRow}>
        <div className={cardDetailStyles.fixedTitleSection}>
          <Tooltip
            content={project.name || "-"}
            relationship="description"
            appearance="inverted"
            positioning="above-start"
            withArrow
            visible={titleOverflow.visible}
          >
            <div
              ref={titleOverflow.ref}
              onMouseEnter={titleOverflow.onMouseEnter}
              onMouseLeave={titleOverflow.onMouseLeave}
              className={cardDetailStyles.title}
            >
              {project.name || "-"}
            </div>
          </Tooltip>
          <Tooltip
            content={project.clientName || "-"}
            relationship="description"
            appearance="inverted"
            positioning="above-start"
            withArrow
            visible={project.clientName ? clientOverflow.visible : false}
          >
            <div
              ref={clientOverflow.ref}
              onMouseEnter={clientOverflow.onMouseEnter}
              onMouseLeave={clientOverflow.onMouseLeave}
              className={mergeClasses(
                cardDetailStyles.subtitle,
                !project.clientName && cardDetailStyles.emptyValue,
              )}
              {...(!project.clientName && {
                "aria-label": "Not available",
              })}
            >
              {project.clientName || "-"}
            </div>
          </Tooltip>
        </div>
        <ProjectActionsMenu
          project={project}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </div>

      <Tooltip
        relationship="description"
        content={project.objective || "-"}
        appearance="inverted"
        positioning="above-start"
        withArrow
        visible={project.objective ? descOverflow.visible : false}
      >
        <div
          ref={descOverflow.ref}
          onMouseEnter={descOverflow.onMouseEnter}
          onMouseLeave={descOverflow.onMouseLeave}
          className={mergeClasses(
            cardDetailStyles.fixedDescription,
            !project.objective && cardDetailStyles.emptyValue,
          )}
        >
          {project.objective || "-"}
        </div>
      </Tooltip>

      <div className={cardDetailStyles.metadataSection}>
        <div className={cardDetailStyles.metadataRow}>
          <span className={cardDetailStyles.metadataLabel}>Owner</span>
          <span
            className={mergeClasses(
              cardDetailStyles.metadataValue,
              !project.createdBy?.displayName && cardDetailStyles.emptyValue,
            )}
            {...(!project.createdBy?.displayName && {
              "aria-label": "Not available",
            })}
          >
            {project.createdBy?.displayName || "-"}
          </span>
        </div>

        <div className={cardDetailStyles.metadataRow}>
          <span className={cardDetailStyles.metadataLabel}>Collaborators</span>
          {collaborators.length > 0 ? (
            <Tooltip relationship="label" content={fullCollaboratorsText}>
              <span className={cardDetailStyles.metadataValue}>
                {collaboratorsText || ConstantValues.EMPTY_STRING}
                {remainingCount > 0 && ` +${remainingCount}`}
              </span>
            </Tooltip>
          ) : (
            <span
              className={mergeClasses(
                cardDetailStyles.metadataValue,
                cardDetailStyles.emptyValue,
              )}
              aria-label="Not available"
            >
              -
            </span>
          )}
        </div>

        <div className={cardDetailStyles.metadataRow}>
          <span className={cardDetailStyles.metadataLabel}>Updated</span>
          <span className={cardDetailStyles.metadataValue}>
            {updatedDate || "-"}
          </span>
        </div>

        <div className={cardDetailStyles.metadataRow}>
          {hasScoringError ? (
            <Tooltip relationship="label" content="Re-score the project">
              <span
                className={mergeClasses(tagStyles.tag, getStatusClass())}
                tabIndex={0}
                role="status"
                aria-label={`Status: ${getStatusDisplayText(project.status)}. Re-score the project`}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onScore(project);
                  }
                }}
              >
                <span
                  className={mergeClasses(
                    styles.statusDot,
                    getStatusDotClass(),
                  )}
                />
                {getStatusDisplayText(project.status)}
              </span>
            </Tooltip>
          ) : (
            <span
              className={mergeClasses(tagStyles.tag, getStatusClass())}
              role="status"
              aria-label={`Status: ${getStatusDisplayText(project.status)}`}
            >
              <span
                className={mergeClasses(styles.statusDot, getStatusDotClass())}
              />
              {getStatusDisplayText(project.status)}
            </span>
          )}
        </div>
      </div>

      <div className={cardDetailStyles.footer}>
        <Tooltip relationship="label" content={getScoreTooltip(project)}>
          <Button
            size="small"
            appearance="subtle"
            icon={<PlayRegular />}
            onClick={() => onScore(project)}
            disabled={!isScoreButtonEnabled}
            aria-label={getScoreButtonLabel(project)}
          />
        </Tooltip>
        <Tooltip
          relationship="label"
          content={
            isScored
              ? PROJECT_TOOLTIPS.OPEN.ENABLED
              : PROJECT_TOOLTIPS.OPEN.DISABLED
          }
        >
          <Button
            size="small"
            appearance="subtle"
            icon={<OpenRegular />}
            onClick={() => onOpen(project)}
            disabled={!isScored}
            aria-label="Open project"
          />
        </Tooltip>
        <Tooltip
          relationship="label"
          content={
            isScored
              ? PROJECT_TOOLTIPS.EXPORT.ENABLED
              : PROJECT_TOOLTIPS.EXPORT.DISABLED
          }
        >
          <Button
            size="small"
            appearance="subtle"
            icon={<ArrowDownloadRegular />}
            onClick={() => onExport(project)}
            disabled={!isScored}
            aria-label="Export project"
          />
        </Tooltip>
      </div>
    </div>
  );
};
